import React, { useMemo } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Field, useForm, useFormState } from 'react-final-form';
import CheckToggleField from 'components/CheckToggleField';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { AvailabilityActionFormUtility } from 'router/subrouters/DataRoom/models/DataRoomBulk';
import PreviewWatermark from '../PreviewWatermark';
import { updateCheckboxValue } from '../../../../checkboxUtils';

import './WatermarkSection.styles.scss';
import './WatermarkSection.styles.responsive.scss';

type WatermarkSectionProps = {
  className?: string;
};

const isTrue = ({ checked, isMixed }: AvailabilityActionFormUtility) =>
  checked || isMixed;

const WatermarkSection: React.FC<WatermarkSectionProps> = (props) => {
  const { className } = props;

  const classes = classNames('watermark-section', className);

  const { t } = useTranslation();

  const { values } = useFormState();

  const { change } = useForm();

  const {
    includeWatermark,
    watermarkDiagonal,
    watermarkHeader,
    watermarkFooter,
    watermarkFullName,
    watermarkEmail,
    watermarkIpAddress,
  } = values || {};

  const isIncludeWatermarkChecked = includeWatermark?.checked;

  const params = useMemo(
    () => ({
      watermarkDiagonal: isTrue(watermarkDiagonal),
      watermarkHeader: isTrue(watermarkHeader),
      watermarkFooter: isTrue(watermarkFooter),
      watermarkFullName: isTrue(watermarkFullName),
      watermarkEmail: isTrue(watermarkEmail),
      watermarkIpAddress: isTrue(watermarkIpAddress),
    }),
    [
      watermarkDiagonal,
      watermarkEmail,
      watermarkFooter,
      watermarkFullName,
      watermarkHeader,
      watermarkIpAddress,
    ],
  );

  return (
    <div className={classes}>
      <p className="title">{t('watermark')}</p>

      <div className="watermark-section__heading">
        <Field
          name="includeWatermark.checked"
          component={CheckToggleField}
          label={t('watermarkFile')}
          type="checkbox"
        />

        <PreviewWatermark
          disabled={!isIncludeWatermarkChecked}
          params={params}
        />
      </div>

      {isIncludeWatermarkChecked && (
        <div className="watermark-section__content">
          <div>
            <p>{t('appearance')}</p>
            <Field
              component={CheckboxField}
              label={t('watermarkOptionDiagonal')}
              name="watermarkDiagonal.checked"
              type="checkbox"
              className={classNames({ mixed: watermarkDiagonal?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'watermarkDiagonal',
                  checked,
                  state: watermarkDiagonal?.state,
                  isMixed: watermarkDiagonal?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
            <Field
              component={CheckboxField}
              label={t('watermarkOptionHeader')}
              name="watermarkHeader.checked"
              type="checkbox"
              className={classNames({ mixed: watermarkHeader?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'watermarkHeader',
                  checked,
                  state: watermarkHeader?.state,
                  isMixed: watermarkHeader?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
            <Field
              component={CheckboxField}
              label={t('watermarkOptionFooter')}
              name="watermarkFooter.checked"
              type="checkbox"
              className={classNames({ mixed: watermarkFooter?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'watermarkFooter',
                  checked,
                  state: watermarkFooter?.state,
                  isMixed: watermarkFooter?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
          </div>

          <div>
            <p>{t('includeTheUsers')}:</p>
            <Field
              component={CheckboxField}
              label={t('name')}
              name="watermarkFullName.checked"
              type="checkbox"
              className={classNames({ mixed: watermarkFullName?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'watermarkFullName',
                  checked,
                  state: watermarkFullName?.state,
                  isMixed: watermarkFullName?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
            <Field
              component={CheckboxField}
              label={t('email')}
              name="watermarkEmail.checked"
              type="checkbox"
              className={classNames({ mixed: watermarkEmail?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'watermarkEmail',
                  checked,
                  state: watermarkEmail?.state,
                  isMixed: watermarkEmail?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
            <Field
              component={CheckboxField}
              label={t('ipAddressAndTimestamp')}
              name="watermarkIpAddress.checked"
              type="checkbox"
              className={classNames({ mixed: watermarkIpAddress?.isMixed })}
              onChange={(checked: boolean) =>
                updateCheckboxValue({
                  name: 'watermarkIpAddress',
                  checked,
                  state: watermarkIpAddress?.state,
                  isMixed: watermarkIpAddress?.isMixed,
                  canDowngrade: true,
                  onChange: change,
                })
              }
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default WatermarkSection;
